import { encodeBase64, decodeBase64, encodeBase64Url } from '../crypto/primitives';
import { csrfHeadersForRequest, requireCsrfHeaders } from './csrf';

export interface SessionResponse {
  userId: string;
  displayName: string;
  email: string | null;
  roles: string[];
  vaultSetupComplete: boolean;
  vaultVersion: number;
  locale?: string;
}

export type EmailNotificationType =
  | 'infoShareReplyReceived'
  | 'recoveryPackageCreated'
  | 'recoveryPackageReplaced'
  | 'recoveryPackageInvalidated'
  | 'sharedFolderMemberInvited'
  | 'sedrMemberInvited'
  | 'sedrGuestRoomAdded'
  | 'sedrGuestAccountReady'
  | 'sedrDeletionRequested'
  | 'sedrDeletionPending'
  | 'sharedFolderExpiryDigest';

export interface EmailNotificationPreference {
  type: EmailNotificationType;
  enabled: boolean;
}

export interface EmailNotificationPreferencesResponse {
  preferences: EmailNotificationPreference[];
}

export interface SedrDigestRoomPreference {
  roomId: string;
  title: string;
  dailyEnabled: boolean;
  biweeklyEnabled: boolean;
}

export interface SedrDigestPreferencesResponse {
  dailyEnabled: boolean;
  biweeklyEnabled: boolean;
  rooms: SedrDigestRoomPreference[];
}

export interface SedrDigestRoomPreferenceUpdate {
  roomId: string;
  dailyEnabled?: boolean;
  biweeklyEnabled?: boolean;
}

export interface UpdateSedrDigestPreferencesRequest {
  dailyEnabled?: boolean;
  biweeklyEnabled?: boolean;
  rooms?: SedrDigestRoomPreferenceUpdate[];
}

export interface DevPersona {
  id: string;
  displayName: string;
  roles: string[];
}

export interface VaultStatusResponse {
  vaultSetupComplete: boolean;
  vaultVersion: number;
  hasRecoveryPackage: boolean;
  recoveryGeneration: number;
  recoveryActive: boolean;
}

export interface VaultBlobResponse {
  vaultBlob: string;
  vaultSalt: string;
  vkWrapPassphrase: string;
  vaultVersion: number;
  vaultSetupComplete: boolean;
  passphraseSetAt: string | null;
}

export type PasswordPolicyTarget =
  | 'vaultPassphrase'
  | 'guestVaultPassphrase'
  | 'infoSharePassword'
  | 'folderGuestPassword'
  | 'sedrGuestPassword';

export interface PasswordPolicy {
  id: string;
  name: string;
  appliesTo: PasswordPolicyTarget[];
  minLength: number;
  maxLength: number;
  requireUppercase: boolean;
  requireLowercase: boolean;
  requireDigit: boolean;
  requireSpecial: boolean;
  minCharacterClasses: number;
  bannedWords: string[];
  blockCommonPasswords: boolean;
  disallowEmailLocalPart: boolean;
  maxRepeatedChars: number | null;
  maxSequentialChars: number | null;
  maxAgeDays: number | null;
}

export interface StartBlobUploadResponse {
  uploadId: string;
  maxChunkSizeBytes: number;
  maxFileSizeBytes: number;
  expiresAt: string;
}

export interface BlobUploadStatusResponse {
  uploadId: string;
  uploadedChunkIndices: number[];
  totalSizeBytes: number;
  expiresAt: string;
  completed: boolean;
}

export interface CompleteBlobUploadResponse {
  blobId: string;
  sizeBytes: number;
}

export interface SharedFolderSummary {
  id: string;
  serverToken: string;
  displayStatus: string;
  createdAt: string;
  updatedAt: string;
  expiresAt: string;
  permission: string;
  manifestRevision: number;
  wrappedFolderKey: string;
  labelBlobId: string | null;
  canExtend: boolean;
  maxExtendDays: number;
  lastViewedAt: string | null;
}

export interface SharedFolderDetail extends SharedFolderSummary {
  canManageMembers: boolean;
  canCommitManifest: boolean;
  activityBlobId?: string | null;
  activityRevision?: number;
}

export interface SharedFolderActivityMeta {
  activityBlobId: string | null;
  activityRevision: number;
}

export interface SharedFolderMember {
  userId: string;
  displayName: string;
  permission: string;
  createdAt: string;
}

export interface SharedFolderGuestInviteSummary {
  id: string;
  permission: string;
  label: string | null;
  revoked: boolean;
  createdAt: string;
  maxGuestUploadFiles: number | null;
  maxGuestUploadTotalBytes: number | null;
  guestUploadFilesUsed: number;
  guestUploadBytesUsed: number;
  rootPath: string | null;
  inviteKind: 'Standard' | 'QuickShare' | string;
}

function normalizeGuestInviteSummary(
  invite: SharedFolderGuestInviteSummary,
): SharedFolderGuestInviteSummary {
  return {
    ...invite,
    maxGuestUploadFiles: invite.maxGuestUploadFiles ?? null,
    maxGuestUploadTotalBytes: invite.maxGuestUploadTotalBytes ?? null,
    label: invite.label ?? null,
    rootPath: invite.rootPath ?? null,
    inviteKind: invite.inviteKind ?? 'Standard',
  };
}

export interface CreateSharedFolderGuestInviteResponse {
  guestInviteId: string;
  guestPath: string;
  expiresAt: string;
}

export interface SharedFolderManifest {
  manifestBlobId: string;
  revision: number;
}

export interface CreateSharedFolderResponse {
  id: string;
  serverToken: string;
  guestPathPrefix: string;
  expiresAt: string;
  manifestRevision: number;
}

export interface SharedFolderFileChunkResult {
  bytes: Uint8Array;
  totalSizeBytes: number;
  totalChunks: number;
}

export interface SharedFolderDeletionHistoryEntry {
  id: string;
  revision: number;
  manifestBlobId: string;
  deletedAt: string;
  purgeAt: string;
  actorKind: string | null;
  actorId: string | null;
}

export interface SharedFolderGateResponse {
  nonce: string;
  passwordRequired: boolean;
  passwordMode: string;
  permission: string;
  expired: boolean;
  revoked: boolean;
  expiresAt: string;
  folderId: string;
  maxGuestUploadFiles: number | null;
  maxGuestUploadTotalBytes: number | null;
  guestUploadFilesUsed: number;
  guestUploadBytesUsed: number;
}

export interface CreateInfoShareResponse {
  id: string;
  serverToken: string;
  guestPath: string;
  expiresAt: string;
  smsDeliveryReference?: string | null;
}

export interface InfoShareSummary {
  id: string;
  serverToken: string;
  displayStatus: string;
  createdAt: string;
  expiresAt: string;
  multiUseAccess: boolean;
  allowGuestReply: boolean;
  passwordMode: string;
  unreadReplyCount: number;
  labelBlobId: string | null;
  wasOpened: boolean;
  smsDeliveryReference?: string | null;
  smsDeliveryStatus?: string | null;
  smsDeliveryErrorDescription?: string | null;
}

export interface InfoShareReplySummary {
  id: string;
  createdAt: string;
  opened: boolean;
  attachmentCount: number;
  attachmentBytes: number;
  purgeAt: string | null;
}

export interface InfoShareReplyDetail extends InfoShareReplySummary {
  wrappedContentKey: string;
  manifestBlobId: string;
}

export interface InfoShareReplyKeyResponse {
  infoShareId: string;
  ownerUserId: string;
  replyEncryptionPublicKey: string;
  replyAllowsFiles: boolean;
  remainingReplyFiles: number | null;
  remainingReplyBytes: number | null;
}

export interface InfoShareCreateDefaults {
  expiresDays: number;
  multiUseAccess: boolean;
  passwordMode: string;
  allowGuestReply: boolean;
  replyAllowsFiles: boolean;
  maxReplyFiles: number | null;
  maxReplyTotalBytes: number | null;
  replyRetentionDaysAfterOpen: number;
}

export interface InfoShareDetail {
  id: string;
  serverToken: string;
  displayStatus: string;
  createdAt: string;
  expiresAt: string;
  multiUseAccess: boolean;
  allowGuestReply: boolean;
  replyAllowsFiles: boolean;
  maxReplyFiles: number | null;
  maxReplyTotalBytes: number | null;
  replyRetentionDaysAfterOpen: number;
  replyFilesUsed: number;
  replyBytesUsed: number;
  passwordMode: string;
  canEdit: boolean;
  labelBlobId: string | null;
  wasOpened: boolean;
}

export interface InfoShareGateResponse {
  nonce: string;
  passwordRequired: boolean;
  passwordMode: string;
  multiUseAccess: boolean;
  expired: boolean;
  revoked: boolean;
  sealed: boolean;
  expiresAt: string;
  allowGuestReply: boolean;
  replyAllowsFiles: boolean;
  remainingReplyFiles: number | null;
  remainingReplyBytes: number | null;
}

export interface InfoShareOpenResponse {
  opened: boolean;
  alreadyOpened: boolean;
  multiUseAccess: boolean;
}

export interface AuditEvent {
  id: string;
  eventType: string;
  actorUserId: string | null;
  actorGuestSessionId: string | null;
  resourceType: string | null;
  resourceId: string | null;
  detailsJson: string | null;
  createdAt: string;
  actorDisplay: string | null;
  resourceDisplay: string | null;
  targetDisplay: string | null;
}

export interface AuditEventListResponse {
  items: AuditEvent[];
  hasMore: boolean;
  totalCount: number;
}

export interface AdminUserSummary {
  userId: string;
  displayName: string;
}

export interface AdminUserListResponse {
  items: AdminUserSummary[];
}

export interface ListAuditEventsParams {
  limit?: number;
  before?: string;
  from?: string;
  to?: string;
  module?: string;
  eventType?: string;
  actorUserId?: string;
}

export interface DiskUsageResponse {
  blobBytes: number;
  databaseBytes: number;
  totalBytes: number;
  deploymentWarningBytes: number;
  warningThresholdReached: boolean;
  dataPathInsideInstallDirectory: boolean;
}

export type StorageModuleKey = 'sedr' | 'sharedFolders' | 'infoShare' | 'other';
export type StorageActorKind = 'user' | 'guest' | 'unknown';

export interface StorageModuleSlice {
  module: StorageModuleKey | string;
  bytes: number;
}

export interface StorageActorSlice {
  actorKind: StorageActorKind | string;
  userId?: string | null;
  guestSessionId?: string | null;
  displayName: string;
  bytes: number;
}

export interface StorageAnalyticsResponse {
  modules: StorageModuleSlice[];
  actors: StorageActorSlice[];
  attributedBlobBytes: number;
}

export interface StorageSharedFolderItem {
  folderId: string;
  shortId: string;
  ownerUserId: string;
  ownerDisplayName: string;
  createdAt: string;
  totalBytes: number;
}

export interface StorageSharedFolderListResponse {
  items: StorageSharedFolderItem[];
}

export interface StorageContributor {
  actorKind: StorageActorKind | string;
  userId?: string | null;
  guestSessionId?: string | null;
  displayName: string;
  bytes: number;
}

export interface StorageContributorListResponse {
  items: StorageContributor[];
}

export interface StorageSedrRoomItem {
  roomId: string;
  title: string;
  ownerUserId: string;
  ownerDisplayName: string;
  createdAt: string;
  totalBytes: number;
}

export interface StorageSedrRoomListResponse {
  items: StorageSedrRoomItem[];
}

export interface StorageInfoShareOwner {
  ownerUserId: string;
  ownerDisplayName: string;
  messageBytes: number;
  replyBytes: number;
  totalBytes: number;
}

export interface StorageInfoShareByOwnerResponse {
  items: StorageInfoShareOwner[];
}

export interface SystemInfoResponse {
  version: string;
  environment: string;
  versionReportingEnvironment: string;
  versionReportingEnabled: boolean;
  versionReportingProxyConfigured: boolean;
  lastReportedVersion?: string | null;
  lastReportedAt?: string | null;
  hostname: string;
  osDescription: string;
  osArchitecture: string;
  processArchitecture: string;
  frameworkDescription: string;
  processStartedAtUtc: string;
  databasePath: string;
  blobStoragePath: string;
}

export interface ReplyBacklogEntry {
  userId: string;
  displayName: string;
  unopenedCount: number;
  totalReplyBytes: number;
}

export interface ReplyBacklogListResponse {
  items: ReplyBacklogEntry[];
}

export interface RecoveryUser {
  userId: string;
  displayName: string;
  email?: string | null;
}

export interface RecoveryUserListResponse {
  items: RecoveryUser[];
}

export interface SystemSettingsResponse {
  guestVerifyLockout: {
    failureWindowMinutes: number;
    maxFailuresPerLink: number;
    lockoutMinutes: number;
    maxRequestsPerIp: number;
  };
  fileSharing: {
    maxFolderExtensionDays: number;
    maxFolderLifetimeDays: number;
    fileDeleteRetentionDays: number;
    folderExpiryWarningDays: number;
    folderExpiryWarningSendTimeUtc: string;
    maxSubfolderDepth: number;
  };
  informationSharing: {
    incompleteDownloadFallbackSeconds: number;
    unopenedReplyRetentionDays: number;
  };
  storage: {
    deploymentWarningBytes: number;
  };
  guestSession: {
    idleTtlMinutes: number;
    maxLifetimeHours: number;
  };
  upload: {
    sessionIdleTtlHours: number;
  };
  staffEmailDomains: string[];
  passwordPolicies: PasswordPolicy[];
  smtp: {
    configured: boolean;
    enabled: boolean;
    usesDevLogSink: boolean;
    fromAddress: string;
    fromDisplayName: string;
    host: string | null;
    port: number;
    useTls: boolean;
  };
  cmComBudget: {
    enabled: boolean;
    logicalAccountId: string | null;
    creditValidityDays: number;
    lowBalanceThreshold: number;
    expiryWarningDays: number;
    pollSendTimeUtc: string;
    opsPhoneNumbers: string[];
    seededLastTopUpDate: string | null;
  };
  cmComBudgetStatus: {
    smsConfigured: boolean;
    usesDevLogSink: boolean;
    lastAmount: number | null;
    currency: string | null;
    lastPolledAt: string | null;
    lastIncreaseAt: string | null;
    firstSeenAt: string | null;
    estimatedExpiryAt: string | null;
    lastAlertKind: string | null;
    lastAlertedAt: string | null;
    lastPollError: string | null;
  };
  updatedAt: string | null;
}

export interface SystemSettingsPatch {
  guestVerifyLockout?: Partial<SystemSettingsResponse['guestVerifyLockout']>;
  fileSharing?: Partial<SystemSettingsResponse['fileSharing']>;
  informationSharing?: Partial<SystemSettingsResponse['informationSharing']>;
  storage?: Partial<SystemSettingsResponse['storage']>;
  guestSession?: Partial<SystemSettingsResponse['guestSession']>;
  upload?: Partial<SystemSettingsResponse['upload']>;
  staffEmailDomains?: string[];
  cmComBudget?: Partial<SystemSettingsResponse['cmComBudget']>;
  passwordPolicies?: PasswordPolicy[];
}

export interface KeyDirectoryEntry {
  userId: string;
  displayName: string;
  userPublicKey: string;
}

export class ApiError extends Error {
  constructor(
    public readonly code: string,
    message?: string,
    public readonly status: number = 400,
    public readonly fields?: Record<string, string>,
  ) {
    super(message ?? code);
    this.name = 'ApiError';
  }
}

type UnauthorizedHandler = () => void;

let unauthorizedHandler: UnauthorizedHandler | null = null;

/** Register a callback invoked once per 401 from API helpers (avoids app↔api cycles). */
export function setUnauthorizedHandler(handler: UnauthorizedHandler | null): void {
  unauthorizedHandler = handler;
}

/** Notify the app layer when an authenticated request gets HTTP 401. */
export function notifyUnauthorized(status: number): void {
  if (status !== 401) {
    return;
  }
  unauthorizedHandler?.();
}

type ParsedApiErrorBody = {
  code: string;
  fields?: Record<string, string>;
};

export function parseApiErrorBody(
  status: number,
  bodyText: string,
  fallbackCode: string,
): ParsedApiErrorBody {
  try {
    const body = JSON.parse(bodyText) as { code?: string; fields?: Record<string, string> };
    if (body.code) {
      const fields =
        body.fields && typeof body.fields === 'object' && !Array.isArray(body.fields)
          ? body.fields
          : undefined;
      return { code: body.code, fields };
    }
  } catch {
    // ignore parse errors
  }

  if (status === 413) {
    return { code: 'request_too_large' };
  }

  if (status === 429 || status === 503) {
    return { code: 'rate_limited' };
  }

  return { code: fallbackCode };
}

export function parseApiErrorCode(status: number, bodyText: string, fallbackCode: string): string {
  return parseApiErrorBody(status, bodyText, fallbackCode).code;
}

const DEFAULT_REQUEST_TIMEOUT_MS = 15_000;
/** Info-share create may block on CM.com SMS; keep above the provider HttpClient timeout. */
export const INFO_SHARE_CREATE_TIMEOUT_MS = 90_000;
const DEFAULT_TRANSFER_TIMEOUT_MS = 30 * 60_000;

async function fetchWithTimeout(
  path: string,
  init?: RequestInit,
  timeoutMs = DEFAULT_REQUEST_TIMEOUT_MS,
): Promise<Response> {
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), timeoutMs);

  try {
    return await fetch(path, {
      credentials: 'include',
      ...init,
      signal: controller.signal,
      headers: {
        ...csrfHeadersForRequest(path, init?.method),
        ...(init?.headers ?? {}),
      },
    });
  } finally {
    window.clearTimeout(timeout);
  }
}

async function throwApiError(response: Response, fallbackCode: string): Promise<never> {
  notifyUnauthorized(response.status);
  let bodyText = '';
  try {
    bodyText = await response.text();
  } catch {
    // ignore read errors
  }
  const parsed = parseApiErrorBody(response.status, bodyText, fallbackCode);
  throw new ApiError(parsed.code, undefined, response.status, parsed.fields);
}

async function putBlobChunk(
  path: string,
  bytes: Uint8Array,
  onProgress?: (loaded: number, total: number) => void,
): Promise<void> {
  await new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', path);
    xhr.withCredentials = true;
    xhr.timeout = DEFAULT_TRANSFER_TIMEOUT_MS;
    xhr.setRequestHeader('Content-Type', 'application/octet-stream');
    for (const [name, value] of Object.entries(requireCsrfHeaders())) {
      xhr.setRequestHeader(name, value);
    }

    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) {
        onProgress?.(event.loaded, event.total);
      }
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
        return;
      }
      notifyUnauthorized(xhr.status);
      reject(new ApiError(
        parseApiErrorCode(xhr.status, xhr.responseText, 'upload_failed'),
        undefined,
        xhr.status,
      ));
    };
    xhr.onerror = () => reject(new ApiError('upload_failed', undefined, 0));
    xhr.ontimeout = () => reject(new ApiError('upload_failed', undefined, 0));
    xhr.onabort = () => reject(new ApiError('upload_failed', undefined, 0));

    xhr.send(new Blob([bytes.slice()]));
  });
}

async function request<T>(
  path: string,
  init?: RequestInit,
  timeoutMs = DEFAULT_REQUEST_TIMEOUT_MS,
): Promise<T> {
  const response = await fetchWithTimeout(
    path,
    {
      ...init,
      headers: {
        'Content-Type': 'application/json',
        ...(init?.headers ?? {}),
      },
    },
    timeoutMs,
  );

  if (!response.ok) {
    await throwApiError(response, 'request_failed');
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return (await response.json()) as T;
}

async function requestText(path: string, init?: RequestInit, timeoutMs = DEFAULT_TRANSFER_TIMEOUT_MS): Promise<string> {
  const response = await fetchWithTimeout(path, init, timeoutMs);

  if (!response.ok) {
    await throwApiError(response, 'request_failed');
  }

  return response.text();
}

async function requestBytes(path: string, init?: RequestInit, timeoutMs = DEFAULT_TRANSFER_TIMEOUT_MS): Promise<Uint8Array> {
  const response = await fetchWithTimeout(path, init, timeoutMs);

  if (!response.ok) {
    await throwApiError(response, 'request_failed');
  }

  return new Uint8Array(await response.arrayBuffer());
}

export const api = {
  getSession: () => request<SessionResponse>('/api/auth/session'),
  patchMyLocale: (locale: string) =>
    request<SessionResponse>('/api/me/locale', {
      method: 'PATCH',
      body: JSON.stringify({ locale }),
    }),
  getDevPersonas: () => request<DevPersona[]>('/api/auth/dev-personas'),
  devLogin: (persona: string) =>
    request<{ userId: string; displayName: string; roles: string[] }>('/api/auth/dev-login', {
      method: 'POST',
      body: JSON.stringify({ persona }),
    }),
  logout: () => request<void>('/api/auth/logout', { method: 'POST' }),
  guestLogout: () => request<void>('/api/auth/guest-logout', { method: 'POST' }),
  getEmailNotifications: () =>
    request<EmailNotificationPreferencesResponse>('/api/me/email-notifications'),
  putEmailNotifications: (preferences: EmailNotificationPreference[]) =>
    request<EmailNotificationPreferencesResponse>('/api/me/email-notifications', {
      method: 'PUT',
      body: JSON.stringify({ preferences }),
    }),
  getSedrDigestPreferences: () =>
    request<SedrDigestPreferencesResponse>('/api/me/sedr-digest-preferences'),
  putSedrDigestPreferences: (payload: UpdateSedrDigestPreferencesRequest) =>
    request<SedrDigestPreferencesResponse>('/api/me/sedr-digest-preferences', {
      method: 'PUT',
      body: JSON.stringify(payload),
    }),
  getVaultStatus: () => request<VaultStatusResponse>('/api/vault/status'),
  getVaultBlob: () => request<VaultBlobResponse>('/api/vault/blob'),
  devResetVault: () => request<void>('/api/vault/dev-reset', { method: 'POST' }),
  setupVault: (payload: {
    vaultBlob: Uint8Array;
    vaultSalt: Uint8Array;
    vkWrapPassphrase: Uint8Array;
    userPublicKey: Uint8Array;
    vaultVersion: number;
  }) =>
    request<void>('/api/vault/setup', {
      method: 'POST',
      body: JSON.stringify({
        vaultBlob: encodeBase64(payload.vaultBlob),
        vaultSalt: encodeBase64(payload.vaultSalt),
        vkWrapPassphrase: encodeBase64(payload.vkWrapPassphrase),
        userPublicKey: encodeBase64(payload.userPublicKey),
        vaultVersion: payload.vaultVersion,
      }),
    }),
  updateVaultBlob: (vaultBlob: Uint8Array, vaultVersion: number) =>
    request<void>('/api/vault/blob', {
      method: 'PUT',
      body: JSON.stringify({
        vaultBlob: encodeBase64(vaultBlob),
        vaultVersion,
      }),
    }),
  updateVkWrap: (vkWrapPassphrase: Uint8Array) =>
    request<void>('/api/vault/vk-wrap', {
      method: 'PUT',
      body: JSON.stringify({
        vkWrapPassphrase: encodeBase64(vkWrapPassphrase),
      }),
    }),
  putRecoveryPackage: (payload: {
    recoveryPublicKey: Uint8Array;
    recoveryVkWrap: Uint8Array;
    recoveryGeneration: number;
  }) =>
    request<void>('/api/vault/recovery', {
      method: 'PUT',
      body: JSON.stringify({
        recoveryPublicKey: encodeBase64(payload.recoveryPublicKey),
        recoveryVkWrap: encodeBase64(payload.recoveryVkWrap),
        recoveryGeneration: payload.recoveryGeneration,
      }),
    }),
  getRecoveryPackage: () =>
    request<{
      recoveryPublicKey: string;
      recoveryVkWrap: string;
      recoveryGeneration: number;
      recoveryActive: boolean;
    }>('/api/vault/recovery'),
  getKeyDirectory: () => request<KeyDirectoryEntry[]>('/api/vault/key-directory'),
  getDiskUsage: () =>
    request<DiskUsageResponse>('/api/admin/disk-usage'),
  getStorageAnalytics: () =>
    request<StorageAnalyticsResponse>('/api/admin/disk-usage/analytics'),
  listStorageSharedFolders: () =>
    request<StorageSharedFolderListResponse>('/api/admin/disk-usage/shared-folders'),
  listStorageSharedFolderContributors: (folderId: string) =>
    request<StorageContributorListResponse>(
      `/api/admin/disk-usage/shared-folders/${encodeURIComponent(folderId)}/contributors`,
    ),
  listStorageSedrRooms: () =>
    request<StorageSedrRoomListResponse>('/api/admin/disk-usage/sedr-rooms'),
  listStorageSedrRoomContributors: (roomId: string) =>
    request<StorageContributorListResponse>(
      `/api/admin/disk-usage/sedr-rooms/${encodeURIComponent(roomId)}/contributors`,
    ),
  listStorageInfoShareByOwner: () =>
    request<StorageInfoShareByOwnerResponse>('/api/admin/disk-usage/info-share/by-owner'),
  getSystemInfo: () =>
    request<SystemInfoResponse>('/api/admin/system-info'),
  getReplyBacklog: () =>
    request<ReplyBacklogListResponse>('/api/admin/reply-backlog'),
  listRecoveryUsers: () =>
    request<RecoveryUserListResponse>('/api/admin/recovery-users'),
  listAdminUsers: () =>
    request<AdminUserListResponse>('/api/admin/users'),
  getSystemSettings: () =>
    request<SystemSettingsResponse>('/api/admin/settings'),
  updateSystemSettings: (settings: SystemSettingsPatch) =>
    request<SystemSettingsResponse>('/api/admin/settings', {
      method: 'PATCH',
      body: JSON.stringify({ settings }),
    }),
  startBlobUpload: () =>
    request<StartBlobUploadResponse>('/api/blobs/uploads', {
      method: 'POST',
      body: JSON.stringify({ contentType: 'application/octet-stream' }),
    }),
  getBlobUploadStatus: (uploadId: string) =>
    request<BlobUploadStatusResponse>(`/api/blobs/uploads/${uploadId}`),
  uploadBlobChunk: async (
    uploadId: string,
    chunkIndex: number,
    bytes: Uint8Array,
    onProgress?: (loaded: number, total: number) => void,
  ) => {
    await putBlobChunk(
      `/api/blobs/uploads/${uploadId}/chunks/${chunkIndex}`,
      bytes,
      onProgress,
    );
  },
  completeBlobUpload: (uploadId: string, chunkCount: number) =>
    request<CompleteBlobUploadResponse>(`/api/blobs/uploads/${uploadId}/complete`, {
      method: 'POST',
      body: JSON.stringify({ chunkCount }),
    }),
  createInfoShare: (payload: {
    manifestBlobId: string;
    fragmentProofPublicKey: Uint8Array;
    expiresAt: string;
    multiUseAccess: boolean;
    passwordMode: string;
    passwordVerifier?: string;
    smsPhoneNumber?: string;
    smsPassword?: string;
    smsDeliveryReference?: string;
    smsDestinationCiphertext?: string;
    allowGuestReply: boolean;
    replyAllowsFiles: boolean;
    replyRetentionDaysAfterOpen: number;
    maxReplyFiles?: number;
    maxReplyTotalBytes?: number;
    replyEncryptionPublicKey?: Uint8Array;
    labelBlobId?: string;
    attachmentBlobIds?: string[];
  }) =>
    request<CreateInfoShareResponse>(
      '/api/info-shares',
      {
        method: 'POST',
        body: JSON.stringify({
          manifestBlobId: payload.manifestBlobId,
          fragmentProofPublicKey: encodeBase64Url(payload.fragmentProofPublicKey),
          expiresAt: payload.expiresAt,
          multiUseAccess: payload.multiUseAccess,
          passwordMode: payload.passwordMode,
          passwordVerifier: payload.passwordVerifier,
          smsPhoneNumber: payload.smsPhoneNumber,
          smsPassword: payload.smsPassword,
          smsDeliveryReference: payload.smsDeliveryReference,
          smsDestinationCiphertext: payload.smsDestinationCiphertext,
          allowGuestReply: payload.allowGuestReply,
          replyAllowsFiles: payload.replyAllowsFiles,
          replyRetentionDaysAfterOpen: payload.replyRetentionDaysAfterOpen,
          maxReplyFiles: payload.maxReplyFiles,
          maxReplyTotalBytes: payload.maxReplyTotalBytes,
          replyEncryptionPublicKey: payload.replyEncryptionPublicKey
            ? encodeBase64Url(payload.replyEncryptionPublicKey)
            : undefined,
          labelBlobId: payload.labelBlobId,
          attachmentBlobIds: payload.attachmentBlobIds,
        }),
      },
      INFO_SHARE_CREATE_TIMEOUT_MS,
    ),
  listInfoShares: () => request<InfoShareSummary[]>('/api/info-shares/mine'),
  getInfoShareCreateDefaults: () =>
    request<InfoShareCreateDefaults>('/api/info-shares/create-defaults'),
  putInfoShareCreateDefaults: (payload: InfoShareCreateDefaults) =>
    request<InfoShareCreateDefaults>('/api/info-shares/create-defaults', {
      method: 'PUT',
      body: JSON.stringify(payload),
    }),
  getInfoShare: (id: string) => request<InfoShareDetail>(`/api/info-shares/mine/${id}`),
  patchInfoShare: (
    id: string,
    payload: {
      expiresAt?: string;
      allowGuestReply?: boolean;
      replyAllowsFiles?: boolean;
      maxReplyFiles?: number | null;
      maxReplyTotalBytes?: number | null;
      replyRetentionDaysAfterOpen?: number;
      replyEncryptionPublicKey?: Uint8Array;
      labelBlobId?: string | null;
    },
  ) =>
    request<InfoShareDetail>(`/api/info-shares/mine/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({
        expiresAt: payload.expiresAt,
        allowGuestReply: payload.allowGuestReply,
        replyAllowsFiles: payload.replyAllowsFiles,
        maxReplyFiles: payload.maxReplyFiles,
        maxReplyTotalBytes: payload.maxReplyTotalBytes,
        replyRetentionDaysAfterOpen: payload.replyRetentionDaysAfterOpen,
        replyEncryptionPublicKey: payload.replyEncryptionPublicKey
          ? encodeBase64Url(payload.replyEncryptionPublicKey)
          : undefined,
        labelBlobId: payload.labelBlobId,
      }),
    }),
  fetchBlob: (blobId: string) => requestBytes(`/api/blobs/${blobId}`),
  revokeInfoShare: (id: string) =>
    request<void>(`/api/info-shares/mine/${id}/revoke`, { method: 'POST' }),
  getInfoShareGate: (token: string) => requestText(`/api/info-shares/${token}/gate`),
  verifyInfoShare: (token: string, signature: string, password?: string) =>
    request<{ ok: boolean }>(`/api/info-shares/${token}/verify`, {
      method: 'POST',
      body: JSON.stringify({ signature, password }),
    }),
  openInfoShare: (token: string) =>
    request<InfoShareOpenResponse>(`/api/info-shares/${token}/open`, { method: 'POST' }),
  fetchInfoShareManifest: (token: string) => requestBytes(`/api/info-shares/${token}/manifest`),
  completeInfoShareAttachmentDownload: (token: string, blobId: string) =>
    request<void>(`/api/info-shares/${token}/blobs/${encodeURIComponent(blobId)}/download-complete`, {
      method: 'POST',
    }),
  getInfoShareReplyKey: (token: string) =>
    request<InfoShareReplyKeyResponse>(`/api/info-shares/${token}/reply-key`),
  submitInfoShareReply: (
    token: string,
    payload: {
      wrappedContentKey: Uint8Array;
      manifestBlobId: string;
      attachmentCount: number;
      attachmentBytes: number;
      attachmentBlobIds?: string[];
    },
  ) =>
    request<{ replyId: string }>(`/api/info-shares/${token}/replies`, {
      method: 'POST',
      body: JSON.stringify({
        wrappedContentKey: encodeBase64Url(payload.wrappedContentKey),
        manifestBlobId: payload.manifestBlobId,
        attachmentCount: payload.attachmentCount,
        attachmentBytes: payload.attachmentBytes,
        attachmentBlobIds: payload.attachmentBlobIds ?? [],
      }),
    }),
  listInfoShareReplies: (shareId: string) =>
    request<InfoShareReplySummary[]>(`/api/info-shares/mine/${shareId}/replies`),
  getInfoShareReply: (shareId: string, replyId: string) =>
    request<InfoShareReplyDetail>(`/api/info-shares/mine/${shareId}/replies/${replyId}`),
  fetchInfoShareReplyManifest: (shareId: string, replyId: string) =>
    requestBytes(`/api/info-shares/mine/${shareId}/replies/${replyId}/manifest`),
  markInfoShareReplyOpened: (shareId: string, replyId: string) =>
    request<{ firstOpenedAt: string; purgeAt: string }>(
      `/api/info-shares/mine/${shareId}/replies/${replyId}/opened`,
      { method: 'POST' },
    ),
  listAuditEvents: (params?: ListAuditEventsParams) => {
    const search = new URLSearchParams();
    if (params?.limit) {
      search.set('limit', String(params.limit));
    }
    if (params?.before) {
      search.set('before', params.before);
    }
    if (params?.from) {
      search.set('from', params.from);
    }
    if (params?.to) {
      search.set('to', params.to);
    }
    if (params?.module && params.module !== 'all') {
      search.set('module', params.module);
    }
    if (params?.eventType) {
      search.set('eventType', params.eventType);
    }
    if (params?.actorUserId) {
      search.set('actorUserId', params.actorUserId);
    }
    const query = search.toString();
    return request<AuditEventListResponse>(
      `/api/admin/audit-events${query ? `?${query}` : ''}`,
    );
  },
  invalidateUserRecovery: (userId: string) =>
    request<void>(`/api/admin/users/${encodeURIComponent(userId)}/recovery`, {
      method: 'DELETE',
    }),
  emergencyRevokeGuestSessions: (payload: {
    resourceType: 'info_share' | 'shared_folder' | 'sedr_room';
    resourceId: string;
  }) =>
    request<{
      resourceType: string;
      resourceId: string;
      sessionsDeleted: number;
      invitesRevoked: number;
    }>('/api/admin/guest-sessions/revoke', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  listSharedFolders: () => request<SharedFolderSummary[]>('/api/shared-folders'),
  getSharedFolder: (id: string) => request<SharedFolderDetail>(`/api/shared-folders/${id}`),
  createSharedFolder: (payload: {
    manifestBlobId: string;
    wrappedFolderKey: string;
    expiresAt: string;
    labelBlobId?: string;
  }) =>
    request<CreateSharedFolderResponse>('/api/shared-folders', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  patchSharedFolder: (
    id: string,
    payload: {
      expiresAt?: string;
      labelBlobId?: string | null;
    },
  ) =>
    request<SharedFolderDetail>(`/api/shared-folders/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }),
  deleteSharedFolder: (id: string) =>
    request<void>(`/api/shared-folders/${id}`, {
      method: 'DELETE',
    }),
  listSharedFolderMembers: (folderId: string) =>
    request<SharedFolderMember[]>(`/api/shared-folders/${folderId}/members/users`),
  inviteSharedFolderMember: (
    folderId: string,
    payload: { userId: string; permission: string; wrappedFolderKey: string },
  ) =>
    request<void>(`/api/shared-folders/${folderId}/members/users`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  patchSharedFolderMember: (folderId: string, userId: string, permission: string) =>
    request<void>(`/api/shared-folders/${folderId}/members/users/${encodeURIComponent(userId)}`, {
      method: 'PATCH',
      body: JSON.stringify({ permission }),
    }),
  removeSharedFolderMember: (folderId: string, userId: string) =>
    request<void>(`/api/shared-folders/${folderId}/members/users/${encodeURIComponent(userId)}`, {
      method: 'DELETE',
    }),
  listSharedFolderGuestInvites: (folderId: string) =>
    request<SharedFolderGuestInviteSummary[]>(`/api/shared-folders/${folderId}/guest-invites`)
      .then((items) => items.map(normalizeGuestInviteSummary)),
  createSharedFolderGuestInvite: (
    folderId: string,
    payload: {
      permission: string;
      envelopeBlobId: string;
      fragmentProofPublicKey: Uint8Array;
      passwordMode: string;
      passwordVerifier?: string;
      label?: string;
      maxGuestUploadFiles?: number | null;
      maxGuestUploadTotalBytes?: number | null;
      rootPath?: string | null;
      inviteKind?: 'Standard' | 'QuickShare';
    },
  ) =>
    request<CreateSharedFolderGuestInviteResponse>(`/api/shared-folders/${folderId}/guest-invites`, {
      method: 'POST',
      body: JSON.stringify({
        permission: payload.permission,
        envelopeBlobId: payload.envelopeBlobId,
        fragmentProofPublicKey: encodeBase64Url(payload.fragmentProofPublicKey),
        passwordMode: payload.passwordMode,
        passwordVerifier: payload.passwordVerifier,
        label: payload.label,
        maxGuestUploadFiles: payload.maxGuestUploadFiles,
        maxGuestUploadTotalBytes: payload.maxGuestUploadTotalBytes,
        rootPath: payload.rootPath,
        inviteKind: payload.inviteKind,
      }),
    }),
  patchSharedFolderGuestInvite: (
    folderId: string,
    inviteId: string,
    payload: {
      permission?: string;
      maxGuestUploadFiles?: number | null;
      maxGuestUploadTotalBytes?: number | null;
    },
  ) =>
    request<SharedFolderGuestInviteSummary>(
      `/api/shared-folders/${folderId}/guest-invites/${encodeURIComponent(inviteId)}`,
      {
        method: 'PATCH',
        body: JSON.stringify(payload),
      },
    ).then(normalizeGuestInviteSummary),
  revokeSharedFolderGuestInvite: (folderId: string, inviteId: string) =>
    request<void>(`/api/shared-folders/${folderId}/guest-invites/${encodeURIComponent(inviteId)}`, {
      method: 'DELETE',
    }),
  fetchSharedFolderManifest: (id: string) =>
    requestBytes(`/api/shared-folders/${id}/manifest`),
  getSharedFolderManifestMeta: (id: string) =>
    request<SharedFolderManifest>(`/api/shared-folders/${id}/manifest`, {
      headers: { Accept: 'application/json' },
    }),
  getSharedFolderGuestManifestMeta: (folderToken: string, guestInviteId: string) =>
    request<SharedFolderManifest>(
      `/api/shared-folders/${folderToken}/g/${guestInviteId}/manifest`,
      { headers: { Accept: 'application/json' } },
    ),
  commitSharedFolderManifest: (
    id: string,
    payload: {
      expectedRevision: number;
      manifestBlobId: string;
      fileBlobRefs?: Array<{ fileId: string; blobId: string }>;
    },
  ) =>
    request<{ revision: number }>(`/api/shared-folders/${id}/manifest`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    }),
  commitSharedFolderGuestManifest: (
    folderToken: string,
    guestInviteId: string,
    payload: {
      expectedRevision: number;
      manifestBlobId: string;
      fileBlobRefs?: Array<{ fileId: string; blobId: string }>;
    },
  ) =>
    request<{
      revision: number;
      guestUploadFilesUsed?: number;
      guestUploadBytesUsed?: number;
    }>(`/api/shared-folders/${folderToken}/g/${guestInviteId}/manifest`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    }),
  getSharedFolderActivity: (id: string) =>
    request<SharedFolderActivityMeta>(`/api/shared-folders/${id}/activity`),
  commitSharedFolderActivity: (
    id: string,
    payload: { expectedRevision: number; activityBlobId: string },
  ) =>
    request<{ revision: number }>(`/api/shared-folders/${id}/activity`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    }),
  getSharedFolderGuestActivity: (folderToken: string, guestInviteId: string) =>
    request<SharedFolderActivityMeta>(
      `/api/shared-folders/${folderToken}/g/${guestInviteId}/activity`,
    ),
  commitSharedFolderGuestActivity: (
    folderToken: string,
    guestInviteId: string,
    payload: { expectedRevision: number; activityBlobId: string },
  ) =>
    request<{ revision: number }>(
      `/api/shared-folders/${folderToken}/g/${guestInviteId}/activity`,
      {
        method: 'PUT',
        body: JSON.stringify(payload),
      },
    ),
  getSharedFolderDeletionHistory: (id: string) =>
    request<SharedFolderDeletionHistoryEntry[]>(`/api/shared-folders/${id}/deletion-history`),
  revertSharedFolderDeletion: (
    id: string,
    payload: {
      expectedRevision: number;
      manifestBlobId: string;
      fileBlobRefs?: Array<{ fileId: string; blobId: string }>;
      historyEntryId?: string;
    },
  ) =>
    request<{ revision: number }>(`/api/shared-folders/${id}/revert`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  fetchSharedFolderFileChunk: async (folderId: string, fileId: string, chunkIndex: number) => {
    const response = await fetchWithTimeout(
      `/api/shared-folders/${folderId}/files/${fileId}/chunks/${chunkIndex}`,
      undefined,
      DEFAULT_TRANSFER_TIMEOUT_MS,
    );

    if (!response.ok) {
      await throwApiError(response, 'request_failed');
    }

    return {
      bytes: new Uint8Array(await response.arrayBuffer()),
      totalSizeBytes: Number(response.headers.get('X-Total-Size-Bytes') ?? 0),
      totalChunks: Number(response.headers.get('X-Total-Chunks') ?? 1),
    } satisfies SharedFolderFileChunkResult;
  },
  fetchSharedFolderGuestFileChunk: async (
    folderToken: string,
    guestInviteId: string,
    fileId: string,
    chunkIndex: number,
  ) => {
    const response = await fetchWithTimeout(
      `/api/shared-folders/${folderToken}/g/${guestInviteId}/files/${fileId}/chunks/${chunkIndex}`,
      undefined,
      DEFAULT_TRANSFER_TIMEOUT_MS,
    );

    if (!response.ok) {
      await throwApiError(response, 'request_failed');
    }

    return {
      bytes: new Uint8Array(await response.arrayBuffer()),
      totalSizeBytes: Number(response.headers.get('X-Total-Size-Bytes') ?? 0),
      totalChunks: Number(response.headers.get('X-Total-Chunks') ?? 1),
    } satisfies SharedFolderFileChunkResult;
  },
  getSharedFolderGate: (folderToken: string, guestInviteId: string) =>
    requestText(`/api/shared-folders/${folderToken}/g/${guestInviteId}/gate`),
  verifySharedFolderGuest: (
    folderToken: string,
    guestInviteId: string,
    signature: string,
    password?: string,
  ) =>
    request<{ verified: boolean }>(`/api/shared-folders/${folderToken}/g/${guestInviteId}/verify`, {
      method: 'POST',
      body: JSON.stringify({ signature, password }),
    }),
  fetchSharedFolderKeyEnvelope: (folderToken: string, guestInviteId: string) =>
    request<{ envelopeBlobId: string }>(
      `/api/shared-folders/${folderToken}/g/${guestInviteId}/key-envelope`,
    ),
  fetchSharedFolderGuestManifest: (folderToken: string, guestInviteId: string) =>
    requestBytes(`/api/shared-folders/${folderToken}/g/${guestInviteId}/manifest`),
  sendSms: (payload: {
    phoneNumber: string;
    message: string;
    reference: string;
    destinationCiphertext: string;
  }) =>
    request<{ sent: boolean; reference?: string }>('/api/sms/send', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  getSmsStatus: () =>
    request<{ provider: string; configured: boolean; usesDevLogSink: boolean }>('/api/sms/status'),
  getSmsDelivery: (reference: string) =>
    request<{
      reference: string;
      status: string;
      cmStatusCode: number | null;
      errorCode: string | null;
      errorDescription: string | null;
      destinationCiphertext: string;
      createdAt: string;
      updatedAt: string;
      failedAt: string | null;
    }>(`/api/sms/delivery/${encodeURIComponent(reference)}`),
  simulateSmsStatus: (payload: {
    reference: string;
    cmStatusCode: number;
    errorCode?: string | null;
    errorDescription?: string | null;
  }) =>
    request<void>('/api/sms/dev/simulate-status', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  listSmsDeliveryFailures: () =>
    request<{
      failures: Array<{
        reference: string;
        status: string;
        cmStatusCode: number | null;
        errorCode: string | null;
        errorDescription: string | null;
        destinationCiphertext: string;
        createdAt: string;
        updatedAt: string;
        failedAt: string | null;
      }>;
    }>('/api/sms/delivery-failures'),
  dismissSmsDeliveryFailure: (reference: string) =>
    request<void>(`/api/sms/delivery-failures/${encodeURIComponent(reference)}`, {
      method: 'DELETE',
    }),
};

export function decodeVaultBlobResponse(response: VaultBlobResponse): {
  vaultBlob: Uint8Array;
  vaultSalt: Uint8Array;
  vkWrapPassphrase: Uint8Array;
  vaultVersion: number;
  passphraseSetAt: string | null;
} {
  return {
    vaultBlob: decodeBase64(response.vaultBlob),
    vaultSalt: decodeBase64(response.vaultSalt),
    vkWrapPassphrase: decodeBase64(response.vkWrapPassphrase),
    vaultVersion: response.vaultVersion,
    passphraseSetAt: response.passphraseSetAt ?? null,
  };
}
